/**
 * Reads a dropped folder in the browser. A GIS tool writes a shapefile as four
 * loose files in a folder, and there is nothing to tell that folder apart from a
 * zip of the same four files, so a dropped folder uploads the same way.
 *
 * Only one level is read. The files sitting directly inside the folder are taken,
 * every subfolder is counted and passed over, and nothing is descended into. A
 * folder tree can hold anything, and walking it would send far more than the user
 * meant to drop.
 */
import { toast } from "react-toastify";

import { MAX_DROP_FILES, MAX_FOLDER_FILES, isZip } from "./uploadKeys";
import {
  ZipEntry,
  ZipPreview,
  groupZipEntries,
  quoteName,
  shapefileStem,
  shorten
} from "./zipPreview";

/** Extensions the upload takes out of a folder. Everything else is passed over. */
export const FOLDER_EXTENSIONS = [
  ".shp",
  ".shx",
  ".dbf",
  ".prj",
  ".cpg",
  ".tif",
  ".tiff"
];

/** The two extensions that go up as GeoTIFFs rather than as part of a shapefile. */
const GEO_TIFF_EXTENSIONS = [".tif", ".tiff"];

/**
 * Files the operating system writes beside the user's own. They are not passed
 * over and counted, because the user never put them there and a count of them
 * would put a warning under every folder a Mac or a Windows machine has opened.
 */
const HOUSEKEEPING_NAMES = [".DS_Store", "Thumbs.db"];

/** Said when the browser would not hand over what a dropped folder holds. */
export const FOLDER_READ_FAILED_MESSAGE = `The browser could not read the folder. Drop it again.`;

/** Said when a drop held nothing the upload takes. */
export const NOTHING_TO_UPLOAD_MESSAGE = `The drop holds no file the upload can take. Drop a .zip file, a shapefile (.shp, .shx, .dbf, .prj), or a .tif file.`;

/** Said when a folder holds more files than one level of reading takes. */
export const TOO_MANY_FOLDER_FILES_MESSAGE = `A folder holds more than ${MAX_FOLDER_FILES} files. The upload did not read it. Put the shapefiles in a folder of their own.`;

/** Said when the folders in one drop hold more files than the upload reads in one go. */
export const TOO_MANY_DROP_FILES_MESSAGE = `The folders in the drop hold more than ${MAX_DROP_FILES} files. The upload did not read them. Drop fewer folders at a time.`;

export interface DroppedFolder {
  /** Identifies the folder across renders, the way fileKeyOf identifies a file. */
  key: string;
  name: string;
  /** The files directly inside it that the upload takes, in the order read. */
  files: File[];
  /** Subfolders that were counted and passed over. */
  ignoredSubfolders: number;
  /** Files inside it whose extension the upload does not take. */
  ignoredFiles: number;
  /** The folder holds more files than one level of reading takes, so none were read. */
  tooManyFiles?: boolean;
}

/** One shapefile the upload can send once, and the copy it had to leave out. */
export interface NameCollision {
  /** The base name of the copy that was left out, as it was written. */
  name: string;
  /** The base name of the copy that was kept, which the server groups the same way. */
  keptName: string;
  /** The folder the later copy came from, unset when it was dropped on its own. */
  folder?: string;
  /** Identifies that folder, because two dropped folders can share a name. */
  folderKey?: string;
  /** The folder the kept copy came from, unset when it was dropped on its own. */
  keptFolder?: string;
  /** Identifies that folder, so a folder that holds both copies is told apart. */
  keptFolderKey?: string;
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot).toLowerCase();
}

function baseNameOf(name: string): string {
  const extension = extensionOf(name);
  return name.slice(0, name.length - extension.length);
}

function isHousekeeping(name: string): boolean {
  return HOUSEKEEPING_NAMES.includes(name) || name.startsWith("._");
}

export function isGeoTiff(name: string): boolean {
  return GEO_TIFF_EXTENSIONS.includes(extensionOf(name));
}

/** What a folder counts against the 500 MB one upload is limited to. */
export function folderBytes(folder: DroppedFolder): number {
  return folder.files.reduce((total, file) => total + file.size, 0);
}

/** Promisify the callback-based FileSystemFileEntry.file() API. */
function fileEntryToFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

/**
 * Reads the entries of a DirectoryReader, up to one past the limit. One readEntries
 * call answers with at most 100 entries, so it is called until it answers with none.
 * One past the limit is enough to know the folder is over it, and the rest of the
 * folder is left unread rather than read and then thrown away.
 */
async function readEntriesUpTo(
  reader: FileSystemDirectoryReader,
  limit: number
): Promise<FileSystemEntry[]> {
  const entries: FileSystemEntry[] = [];
  let batch: FileSystemEntry[];
  do {
    batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    entries.push(...batch);
  } while (batch.length > 0 && entries.length <= limit);
  return entries;
}

/**
 * Counts the folders read since the page loaded, so each read gets a key of its own.
 * A key built out of what a folder holds makes two folders of the same name holding
 * the same file names into one row, and the second one then goes missing with nothing
 * said about it. Two such folders are kept apart here, and the names they share are
 * reported by flattenDrop.
 */
let foldersRead = 0;

/** Reads one level of a dropped folder and counts what it passed over. */
async function readFolder(entry: FileSystemDirectoryEntry): Promise<DroppedFolder> {
  const children = await readEntriesUpTo(entry.createReader(), MAX_FOLDER_FILES);
  foldersRead += 1;

  const folder: DroppedFolder = {
    key: `folder:${foldersRead}`,
    name: entry.name,
    files: [],
    ignoredSubfolders: 0,
    ignoredFiles: 0
  };

  // Reading a file costs a call of its own, and that is the part that would hold the
  // tab, so a folder over the limit is turned down before any of it is read
  if (children.length > MAX_FOLDER_FILES) {
    folder.tooManyFiles = true;
    return folder;
  }

  for (const child of children) {
    if (child.isDirectory) {
      folder.ignoredSubfolders += 1;
      continue;
    }
    if (isHousekeeping(child.name)) continue;
    if (!FOLDER_EXTENSIONS.includes(extensionOf(child.name))) {
      folder.ignoredFiles += 1;
      continue;
    }
    folder.files.push(await fileEntryToFile(child as FileSystemFileEntry));
  }

  return folder;
}

/**
 * Splits a drop into the folders it held and the files that were dropped on their
 * own. A folder is read one level deep; anything else is passed through unchanged,
 * so the drop keeps behaving the way it does today.
 *
 * The item list is read before the first await on purpose. The browser empties it
 * as soon as the drop handler returns, so every entry is taken out of it first.
 */
export async function splitDrop(
  transfer: DataTransfer
): Promise<{ folders: DroppedFolder[]; files: File[] }> {
  const folders: DroppedFolder[] = [];
  const files: File[] = [];

  // A browser with no item list hands over the plain file list and nothing else. It
  // has no entry API either, so it never held a folder to read, and what it dropped
  // loose is the whole drop
  if (!transfer?.items) {
    for (const file of Array.from(transfer?.files ?? [])) {
      if (!isHousekeeping(file.name)) files.push(file);
    }
    return { folders, files };
  }

  const dropped = Array.from(transfer.items)
    .filter((item) => item.kind === "file")
    .map((item) => ({
      entry: item.webkitGetAsEntry?.(),
      // read here as well, because a browser without the entry API has only this
      file: item.getAsFile()
    }));

  let taken = 0;

  for (const { entry, file } of dropped) {
    if (entry?.isDirectory) {
      const folder = await readFolder(entry as FileSystemDirectoryEntry);
      taken += folder.files.length;
      folders.push(folder);
    } else if (file && !isHousekeeping(file.name)) {
      // A file the operating system wrote is left out wherever it was dropped, not
      // only inside a folder. The library's own reader drops these as well, and a
      // user who selects the whole contents of a folder hands them over by hand
      files.push(file);
    }

    // The per-folder limit bounds one folder, and every file goes up as a request of
    // its own, so ten folders under that limit still send thousands of them. Only the
    // folders are counted here: a loose file was picked by hand, and the drop modal
    // never reads a drop that holds no folder, so counting them would refuse a drop on
    // the tree that the same drop on the modal takes. The rest of the drop is left
    // unread and none of it is sent
    if (taken > MAX_DROP_FILES) {
      toast.warning(TOO_MANY_DROP_FILES_MESSAGE);
      return { folders: [], files: [] };
    }
  }

  return { folders, files };
}

/**
 * Answers react-dropzone with the files a drop held, expanding a folder one level
 * the way the drop modal does. Without this the library walks a folder to any
 * depth and sends everything it finds.
 */
export async function getFilesFromDropEvent(
  // the library hands this the drag events and the browse input's change event
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  event: any
): Promise<(File | DataTransferItem)[]> {
  const transfer = event?.dataTransfer;
  if (transfer?.items) {
    const items: DataTransferItem[] = Array.from(transfer.items).filter(
      (item: DataTransferItem) => item.kind === "file"
    ) as DataTransferItem[];
    // The library asks on each drag event as well, and only a drop is allowed to
    // read what is being dragged, so the others are answered with the items
    if (event.type !== "drop") return items;

    // The library hands what comes back to a promise it never catches, so a folder
    // the browser will not read has to be answered for here
    let dropped: { folders: DroppedFolder[]; files: File[] };
    try {
      dropped = await splitDrop(transfer);
    } catch {
      toast.error(FOLDER_READ_FAILED_MESSAGE);
      return [];
    }

    return flattenDropWithToasts(dropped.folders, dropped.files);
  }
  if (transfer?.files) return Array.from(transfer.files) as File[];
  if (event?.target?.files) return Array.from(event.target.files) as File[];
  return [];
}

/**
 * Describes one folder the way an archive is described, so its row reads like a
 * zip's. The shapefile parts go through the archive reader's own grouping, which
 * is what decides whether a .shx or a .dbf is missing, and the GeoTIFFs are counted
 * beside it because they import on their own.
 */
export function describeFolder(folder: DroppedFolder): ZipPreview {
  const entries: ZipEntry[] = [];
  let geoTiffs = 0;

  for (const file of folder.files) {
    if (isGeoTiff(file.name)) {
      geoTiffs += 1;
      continue;
    }
    // nothing is compressed, so both sizes are the size of the file on disk
    entries.push({ name: file.name, size: file.size, compressedSize: file.size });
  }

  return {
    ...groupZipEntries(entries, undefined, { archiveLimits: false }),
    kind: "folder",
    geoTiffs,
    ignoredSubfolders: folder.ignoredSubfolders,
    ignoredFiles: folder.ignoredFiles,
    tooManyFolderFiles: folder.tooManyFiles
  };
}

/** One dropped folder, or every loose file in the drop taken together. */
interface Container {
  /** The folder the files were read from, unset for the loose ones. */
  folder?: { key: string; name: string };
  files: File[];
}

/** One shapefile inside one container, and whether the server would import it. */
interface ContainerGroup {
  container: Container;
  /** What the server would write this group over, which two groups can share. */
  key: string;
  /** The base name as it was written, which is what the user is told about. */
  name: string;
  complete: boolean;
}

/** The groups one container holds, and the group each of its files belongs to. */
interface ContainerGroups {
  groups: ContainerGroup[];
  owner: Map<File, ContainerGroup>;
}

/**
 * Everything in front of the first dot. This deliberately mirrors groupFilesByBaseName
 * in eva-project-svc, which every file out of a folder reaches, so the two are coupled
 * and have to be changed together: it takes the last extension off one at a time until
 * none is left, which is where this ends up, and it keys a plain map with the result,
 * so the case is kept as well.
 */
function strippedName(name: string): string {
  const dot = name.indexOf(".");
  return dot === -1 ? name : name.slice(0, dot);
}

/**
 * The base name the server groups a loose shapefile under, and nothing for a file it
 * never groups that way: an extension it does not keep, or a name that is nothing but
 * extensions, such as ".shp", which leaves no base name behind to group under.
 */
function serverBaseName(name: string): string | undefined {
  if (shapefileStem(name) === undefined) return undefined;
  return strippedName(name) || undefined;
}

/**
 * What the server would write a file over. A shapefile is one thing under the base
 * name the server groups it by, and a file the server never groups, such as a
 * GeoTIFF, stands on its own under its whole name, because a second file of that
 * name still writes over it. A GeoTIFF goes up its own way and never reaches the
 * grouping above, so its whole name keys it and the case is dropped there, the way
 * the rest of the drop matches names.
 */
function groupKeyOf(name: string): string {
  const base = serverBaseName(name);
  return base ? `shapefile:${base}` : `file:${name.toLowerCase()}`;
}

/**
 * What one container holds a file under, which is the shapefile the file is a part
 * of, or the whole name of a file the server never groups. Two names that differ
 * here and still share a group key, such as Roads.2020 beside Roads.2021, are two
 * shapefiles the user dropped and one thing the server would import, so they are
 * held apart here and answered for rather than quietly made into one group.
 */
function containerKeyOf(name: string): string {
  const stem = shapefileStem(name);
  return stem === undefined ? `file:${name}` : `shapefile:${stem}`;
}

/**
 * The shapefiles one container holds, in the order they were read, and the group
 * each of its files belongs to. Whether a group is complete is asked of the archive
 * reader, the same way the folder's own row asks it, so there is one answer to what
 * the server will import and not two that can drift apart.
 */
function groupsOf(container: Container): ContainerGroups {
  const { skipped } = groupZipEntries(
    // nothing is compressed, so both sizes are the size of the file on disk
    container.files.map((file) => ({
      name: file.name,
      size: file.size,
      compressedSize: file.size
    })),
    undefined,
    { archiveLimits: false }
  );
  // The archive reader names a group by the stem in front of the last extension,
  // which is what holds the files of this container apart as well
  const incomplete = new Set(skipped.map((group) => group.name));

  const groups: ContainerGroup[] = [];
  const byContainerKey = new Map<string, ContainerGroup>();
  const owner = new Map<File, ContainerGroup>();
  for (const file of container.files) {
    const containerKey = containerKeyOf(file.name);
    let group = byContainerKey.get(containerKey);
    if (!group) {
      const stem = shapefileStem(file.name);
      group = {
        container,
        key: groupKeyOf(file.name),
        name: baseNameOf(file.name),
        // a file the server never groups is whole on its own
        complete: stem === undefined || !incomplete.has(stem)
      };
      byContainerKey.set(containerKey, group);
      groups.push(group);
    }
    owner.set(file, group);
  }
  return { groups, owner };
}

/**
 * Lays the whole drop out as the one flat list of files that goes to the server,
 * and names what it had to leave out. Only base names reach the server, and it
 * groups a shapefile by its base name, so FolderA/Polygon.shp and
 * FolderB/Polygon.shp arrive as the same file and the second quietly writes over
 * the first.
 *
 * The whole shapefile is what is kept or left out, never the file names that
 * happened to clash. A folder holding Polygon.shp, .shx and .prj beside a folder
 * holding a complete Polygon would otherwise send the first folder's three files
 * with the second folder's .dbf. The server sees a complete shapefile and imports
 * it, and the attributes belong to a different shapefile than the geometry does,
 * which nothing downstream can tell.
 *
 * Two names inside one container are one group on the server as well. Roads.2020
 * and Roads.2021 in one folder are both Roads there, and it builds the layer out of
 * whichever .shp it reads first, so one of the two years is lost and which one is
 * lost changes from run to run. The second of them is left out and reported the
 * same way a second folder's copy is.
 *
 * The complete shapefile is the one kept, because keeping the incomplete one leaves
 * the user with neither. Between two that are both complete, or both incomplete,
 * the first one dropped is kept.
 *
 * An archive is passed through untouched. The server groups it by the base names
 * inside it and never by the archive's own name, so two files called Export.zip
 * hold different shapefiles and both have to go up.
 */
export function flattenDrop(
  folders: DroppedFolder[],
  looseFiles: File[]
): { files: File[]; collisions: NameCollision[] } {
  // The loose files are one container. A shapefile dropped a file at a time is one
  // shapefile being completed, not a second copy of itself, so only two folders, or
  // a folder and the loose files, can ever clash
  const containers: Container[] = [
    { files: looseFiles.filter((file) => !isZip(file.name)) },
    ...folders.map((folder) => ({
      folder: { key: folder.key, name: folder.name },
      files: folder.files
    }))
  ];
  const grouped = containers.map(groupsOf);

  // Which copy is kept is settled before anything is left out, so a folder dropped
  // first can still lose its copy to a complete one dropped after it, and what the
  // user is told names the copy that was kept in the end
  const kept = new Map<string, ContainerGroup>();
  for (const { groups } of grouped) {
    for (const group of groups) {
      const held = kept.get(group.key);
      if (!held || (group.complete && !held.complete)) kept.set(group.key, group);
    }
  }

  // A shapefile that arrives twice arrives four files at a time, and one group is
  // one thing to tell the user about
  const collisions: NameCollision[] = [];
  for (const { groups } of grouped) {
    for (const group of groups) {
      const winner = kept.get(group.key);
      if (!winner || winner === group) continue;
      const collision: NameCollision = { name: group.name, keptName: winner.name };
      if (group.container.folder) {
        collision.folder = group.container.folder.name;
        collision.folderKey = group.container.folder.key;
      }
      if (winner.container.folder) {
        collision.keptFolder = winner.container.folder.name;
        collision.keptFolderKey = winner.container.folder.key;
      }
      collisions.push(collision);
    }
  }

  // The files go out in the order they were dropped, so the archives and the loose
  // files reach the server the way the user sees them in the list
  const files: File[] = [];
  const isKept = (at: number, file: File): boolean => {
    // Two groups in one container can share a key, so the file is matched against
    // the group it was written as and never against that key alone
    const group = grouped[at].owner.get(file);
    return group !== undefined && kept.get(group.key) === group;
  };

  for (const file of looseFiles) {
    if (isZip(file.name)) {
      files.push(file);
      continue;
    }
    if (isKept(0, file)) files.push(file);
  }
  folders.forEach((folder, at) => {
    for (const file of folder.files) {
      if (isKept(at + 1, file)) files.push(file);
    }
  });

  return { files, collisions };
}

/**
 * Names the copies the upload had to leave out. A tree node and the organization
 * drop area have no panel to list them in, so they are named in one sentence, and a
 * drop of several says how many rather than naming each one.
 */
function collisionMessage(collisions: NameCollision[]): string {
  if (collisions.length > 1) {
    return `${collisions.length} files were not added. The upload already holds a file with each of those names.`;
  }

  const { name, keptName, folder, folderKey, keptFolder, keptFolderKey } = collisions[0];
  const shapefile = `${quoteName(name)} shapefile`;
  // The two copies keep different names when the server groups them under one base
  // name, such as Roads.2020 beside Roads.2021. The kept copy is named as well then,
  // because the name of the ignored one says nothing about what survived
  const keptShapefile = `${quoteName(keptName)} shapefile`;
  const sameName = keptName === name;
  const ignored = folder
    ? `The ${shapefile} in ${shorten(folder)} will be ignored.`
    : `The ${shapefile} will be ignored.`;
  // There is no row title to lean on here, so the sentence names both sides. The
  // user tells the two copies apart by the folders they came from
  let kept: string;
  if (keptFolder && keptFolderKey !== folderKey) {
    kept = `${shorten(keptFolder)} has the ${keptShapefile}.`;
  } else if (keptFolder) {
    kept = sameName
      ? `That folder has two of them.`
      : `That folder has the ${keptShapefile}.`;
  } else if (folder) {
    kept = sameName
      ? `It was already dropped on its own.`
      : `The ${keptShapefile} was already dropped on its own.`;
  } else {
    kept = sameName
      ? `The upload already has one.`
      : `The upload already has the ${keptShapefile}.`;
  }
  return `${ignored} ${kept}`;
}

/**
 * Lays a drop out flat and says what it had to leave out. Every drop area outside the
 * modal shares this, because a drop that quietly loses half of itself is the same
 * mistake wherever the user drops it. The modal writes the same things as rows in its
 * issues panel instead.
 */
export function flattenDropWithToasts(
  folders: DroppedFolder[],
  looseFiles: File[]
): File[] {
  const { files, collisions } = flattenDrop(folders, looseFiles);

  const tooManyFiles = folders.some((folder) => folder.tooManyFiles);
  if (tooManyFiles) {
    toast.warning(TOO_MANY_FOLDER_FILES_MESSAGE);
  }
  if (collisions.length > 0) {
    toast.warning(collisionMessage(collisions));
  }
  // An empty folder, a folder of nothing but subfolders, and a folder of files we do
  // not take all reach here with nothing to send. A drop that held nothing at all is
  // not worth a word, and a folder over the limit has already been answered for
  if (
    files.length === 0 &&
    !tooManyFiles &&
    (folders.length > 0 || looseFiles.length > 0)
  ) {
    toast.warning(NOTHING_TO_UPLOAD_MESSAGE);
  }

  return files;
}
